import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/pages/AuthCallback";

// Lazy-load pages to keep initial bundle small
const Home          = lazy(() => import("@/pages/Home"));
const Search        = lazy(() => import("@/pages/Search"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const District      = lazy(() => import("@/pages/District"));
const PropertyType  = lazy(() => import("@/pages/PropertyType"));
const NRI           = lazy(() => import("@/pages/NRI"));
const NRICountry    = lazy(() => import("@/pages/NRICountry"));
const Login         = lazy(() => import("@/pages/Login"));
const Signup        = lazy(() => import("@/pages/Signup"));
const Dashboard     = lazy(() => import("@/pages/Dashboard"));
const CreateListing = lazy(() => import("@/pages/CreateListing"));
const AdminModeration = lazy(() => import("@/pages/AdminModeration"));
const Concierge     = lazy(() => import("@/pages/Concierge"));

const PageLoader = () => (
  <div className="min-h-[60vh] grid place-items-center">
    <div className="h-8 w-8 rounded-full border-2 border-[var(--vp-emerald)] border-t-transparent animate-spin" />
  </div>
);

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <div className="min-h-screen flex flex-col bg-[var(--vp-ivory)] text-[var(--vp-charcoal)]">
          <Navbar />
          <main className="flex-1">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public */}
                <Route path="/"                element={<Home />} />
                <Route path="/search"          element={<Search />} />
                <Route path="/property/:id"    element={<PropertyDetail />} />
                <Route path="/district/:slug"  element={<District />} />
                <Route path="/type/:slug"      element={<PropertyType />} />
                <Route path="/nri"             element={<NRI />} />
                <Route path="/nri/:country"    element={<NRICountry />} />
                <Route path="/concierge"       element={<Concierge />} />
                <Route path="/login"           element={<Login />} />
                <Route path="/signup"          element={<Signup />} />
                <Route path="/list-your-property" element={<Navigate to="/listings/new" replace />} />

                {/* Google OAuth callback — must match redirect_uri in Google Console */}
                <Route path="/auth/google/callback" element={<AuthCallback />} />

                {/* Protected */}
                <Route path="/dashboard" element={
                  <ProtectedRoute><Dashboard /></ProtectedRoute>
                } />
                <Route path="/listings/new" element={
                  <ProtectedRoute><CreateListing /></ProtectedRoute>
                } />
                <Route path="/admin/moderation" element={
                  <ProtectedRoute roles={["admin"]}><AdminModeration /></ProtectedRoute>
                } />

                {/* Fallback */}
                <Route path="*" element={
                  <div className="min-h-[60vh] grid place-items-center">
                    <div className="text-center">
                      <p className="font-heading text-6xl text-[var(--vp-emerald)]">404</p>
                      <p className="mt-2 text-[var(--vp-charcoal-soft)]">Page not found</p>
                      <a href="/" className="mt-4 inline-block text-sm text-[var(--vp-emerald)] underline underline-offset-4">Go home</a>
                    </div>
                  </div>
                } />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
